(ns ycweb.app.website.cart.pages.payment.snapscan
  (:require
   [cemerick.url :as url]
   [rum.core :as rum]
   [ycweb.app.website.cart.model :as model]
   [ycweb.app.website.cart.parts.common :as cart.common]
   [ycweb.components.buttons :as buttons]
   [ycweb.components.common :as cmn]
   [ycweb.components.feedback :as feedback]
   [ycweb.components.toaster :as toaster]
   [ycweb.util.core :as u]
   [ycweb.util.urlstate :as urlstate]))

(def ^:private max-polls 120)

(declare initiate!)

(defn- handle-completed
  "A completed payment either finishes the order or, when SnapScan took less
   than the balance (PHP snapscan.js:32-34 reloads for the remainder), resets
   the screen so a fresh QR is issued for what is still owing."
  [app-atom snapscan-atom order response]
  (let [balance (or (:balance_cents response) 0)]
    (if (pos? balance)
      (do
        (swap! snapscan-atom assoc
          :completed? false :initiated? false :qr-url nil :payment-uuid nil
          :notice (str "Thanks — we received a partial payment. R" (u/currency-format balance)
                    " is still owing; scan the new code to pay the balance."))
        (initiate! app-atom snapscan-atom order))
      (do
        (swap! snapscan-atom assoc :completed? true)
        (swap! app-atom assoc-in [:order-summary :payment-confirmation] :snapscan)
        (swap! (cmn/process-cursor app-atom) assoc :step :order/confirmation)))))

(defn- start-polling
  "Poll the payments table (the webhook writes it) on a widening interval.
   The timeout handle is kept on the atom so unmount can cancel it; after
   max-polls the screen says so instead of going quiet."
  [app-atom payment-uuid order poll-count-atom]
  #?(:cljs
     (let [snapscan-atom (rum/cursor-in app-atom [:cart :snapscan-payment])
           interval
           (cond
             (< @poll-count-atom 30) 2000
             (< @poll-count-atom 60) 5000
             :else 15000)
           handle
           (js/setTimeout
             (fn []
               (swap! snapscan-atom dissoc :poll-handle)
               (cond
                 (or (:completed? @snapscan-atom) (:unmounted? @snapscan-atom)
                   (not= payment-uuid (:payment-uuid @snapscan-atom)))
                 nil

                 (>= @poll-count-atom max-polls)
                 (swap! snapscan-atom assoc :timed-out? true)

                 :else
                 (do
                   (swap! poll-count-atom inc)
                   (model/snapscan-check-status app-atom
                     {:payment-uuid payment-uuid}
                     (fn [response]
                       (if (and (:success response) (= (:payment_status response) "completed"))
                         (handle-completed app-atom snapscan-atom order response)
                         (start-polling app-atom payment-uuid order poll-count-atom)))))))
             interval)]
       (swap! snapscan-atom assoc :poll-handle handle))))

(defn- initiate!
  "Ask the server for a QR (it hands back the same row/QR for a repeat request
   inside the reuse window) and start polling it."
  [app-atom snapscan-atom order]
  #?(:cljs
     (do
       (swap! snapscan-atom assoc :loading? true :initiated? true :timed-out? false :error nil)
       (model/snapscan-initiate app-atom
         {:order-uuid (:uuid order)}
         (fn [response]
           (swap! snapscan-atom assoc :loading? false)
           (if (:success response)
             (do
               (swap! snapscan-atom assoc
                 :payment-uuid (:payment_uuid response)
                 :qr-url (:qr_url response)
                 :amount (:amount response))
               (start-polling app-atom (:payment_uuid response) order (atom 0)))
             (swap! snapscan-atom assoc :error (:msg response))))))))

(rum/defc snapscan-payment < rum/reactive cmn/cursors
  {:will-mount
   (fn [state]
     #?(:cljs
        (let [[app-atom process-atom cart-atom] (:rum/args state)
              order (get-in @cart-atom [:order])
              snapscan-atom (rum/cursor-in app-atom [:cart :snapscan-payment])]
          (swap! snapscan-atom dissoc :unmounted?)
          (when (and order (not (:initiated? @snapscan-atom)))
            (initiate! app-atom snapscan-atom order))))
     state)
   :will-unmount
   (fn [state]
     #?(:cljs
        (let [[app-atom] (:rum/args state)
              snapscan-atom (rum/cursor-in app-atom [:cart :snapscan-payment])]
          (when-let [handle (:poll-handle @snapscan-atom)]
            (js/clearTimeout handle))
          (swap! snapscan-atom assoc :unmounted? true)
          (swap! snapscan-atom dissoc :poll-handle)))
     state)}
  [app-atom process-atom cart-atom]
  (let [snapscan-data (cmn/curact app-atom [:cart :snapscan-payment])
        order (cmn/curact cart-atom [:order])]
    (cart.common/checkout-layout process-atom false
      (list
        (when (:error snapscan-data)
          (rum/with-key (feedback/feedback :error (:error snapscan-data) nil) "snapscan-error"))
        (when (:notice snapscan-data)
          (rum/with-key (feedback/feedback :success (:notice snapscan-data) nil) "snapscan-notice"))
        (when (:timed-out? snapscan-data)
          (rum/with-key
            (feedback/feedback :warning
              "We haven't seen your payment yet."
              "If you've paid, press \"Check payment\" below. Otherwise scan the code again or choose a different payment method.")
            "snapscan-timeout"))
        (rum/with-key
          (feedback/feedback :info
            (str "Amount owing: R" (u/currency-format (cmn/curact cart-atom [:totals :balance])))
            (str "Your order number is " (:number order)))
          "snapscan-info")
        [:h2 {:key "snapscan-heading" :class "yc-heading yc-heading--default"} "Pay with SnapScan"]
        [:p {:key "snapscan-desc" :class "yc-paragraph"} "Scan the QR code below with the SnapScan app to complete your payment."]
        (if (:qr-url snapscan-data)
          [:div {:key "snapscan-qr" :style {:text-align "center" :padding "20px"}}
           [:img {:src (str "https://api.qrserver.com/v1/create-qr-code/?size=250x250&data="
                    (url/url-encode (:qr-url snapscan-data)))
                  :alt "SnapScan QR Code"
                  :width 250 :height 250}]]
          (when (:loading? snapscan-data)
            [:p {:key "snapscan-qr" :class "yc-paragraph"} "Loading SnapScan..."]))
        [:div {:key "snapscan-check"}
         [:p {:class "yc-paragraph"} "When you've paid using the app, check the payment using the button below."]
         (buttons/button-web "Check payment"
           {:style :primary
            :disabled (:checking? snapscan-data)
            :on-click
            (fn [e]
              (.preventDefault e)
              (let [snapscan-atom (rum/cursor-in app-atom [:cart :snapscan-payment])]
                (swap! snapscan-atom assoc :checking? true)
                ;; The manual button is the one place we ask SnapScan directly
                ;; (PHP: "Click here when paid" hits the API-backed path)
                (model/snapscan-check-status app-atom
                  {:payment-uuid (:payment-uuid snapscan-data) :check-api true}
                  (fn [response]
                    (swap! snapscan-atom assoc :checking? false)
                    (if (and (:success response) (= (:payment_status response) "completed"))
                      (handle-completed app-atom snapscan-atom order response)
                      (toaster/open-toast app-atom :warning
                        "Payment not yet received. Please try again."
                        {:closetime 5000 :show-close-button true}))))))})]
        [:p {:key "snapscan-alt" :class "yc-paragraph yc-paragraph--flush u-margin-top-16"}
         "Would you like to choose a "
         [:a.text-link
          (urlstate/href-props app-atom "order" "ShowCart"
            {:orderid (:id order) :h (:h order) :step "payment/method-postcheckout" :method "SnapScan"})
          "different payment method?"]])

      (cart.common/checkout-cta-sidebar-simple
        nil nil (:number order) cart-atom {:total-key :balance}))))

(rum/defc snapscan-page < rum/reactive cmn/cursors [app-atom process-atom cart-atom]
  (snapscan-payment app-atom process-atom cart-atom))
